import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getImageUrl } from "../../utils/imageUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import {
  Store,
  Upload,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Globe,
  Palette,
  Image,
  Loader2,
  X,
  Check,
  ChevronsUpDown,
  Eye,
  EyeOff
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { US_STATES, CA_PROVINCES, COUNTRIES } from "@/utils/locationData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// THEME_PRESETS removed. Now fetched from API.

export default function ResellerSettings({ store, onUpdate }) {
  const [themePresets, setThemePresets] = useState({});
  const [loadingPresets, setLoadingPresets] = useState(true);
  const [formData, setFormData] = useState({
    business_name: store.business_name || "",
    business_description: store.business_description || "",
    logo_url: store.logo_url || "",
    contact_email: store.contact_email || "",
    contact_phone: store.contact_phone || "",
    business_address: {
      address: store.business_address?.address || store.address || "",
      city: store.business_address?.city || "",
      state: store.business_address?.state || "",
      zipcode: store.business_address?.zipcode || "",
      country: store.business_address?.country || "US"
    },
    social_links: store.social_links || {},
    theme: store.theme || { preset: "classic_elegant" },
    show_business_name: store.show_business_name ?? true
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchPresets = async () => {
      try {
        const response = await axios.get(`${API}/theme-presets`);
        setThemePresets(response.data);
      } catch (error) {
        console.error("Failed to fetch theme presets:", error);
        toast.error("Failed to load theme presets");
      } finally {
        setLoadingPresets(false);
      }
    };
    fetchPresets();
  }, []);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, GIF, or WebP.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('logo', file);

      const response = await axios.post(`${API}/upload/logo`, formDataUpload);

      const logoUrl = response.data.url;
      setFormData(prev => ({ ...prev, logo_url: logoUrl }));
      toast.success('Logo uploaded successfully!');
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.detail || 'Failed to upload logo';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = () => {
    setFormData(prev => ({ ...prev, logo_url: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/stores/my`, formData);
      toast.success("Settings updated successfully");
      onUpdate();
    } catch (error) {
      toast.error("Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSocialLinkChange = (platform, url) => {
    setFormData(prev => ({
      ...prev,
      social_links: {
        ...prev.social_links,
        [platform]: url
      }
    }));
  };

  const socialPlatforms = [
    { key: "website", icon: Globe, label: "Website" },
    { key: "facebook", icon: Facebook, label: "Facebook" },
    { key: "twitter", icon: Twitter, label: "Twitter" },
    { key: "instagram", icon: Instagram, label: "Instagram" },
    { key: "linkedin", icon: Linkedin, label: "LinkedIn" },
  ];

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="reseller-settings">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">
          Business Information
        </h2>
        <p className="text-slate-500">Customize your store details and branding</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Info */}
        <div className="card-elevated p-6 space-y-4">
          <h3 className="text-lg font-semibold text-slate-900">
            Basic Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="business_name">Business Name</Label>
              <Input
                id="business_name"
                value={formData.business_name}
                onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))}
                className="h-12"
                data-testid="input-business-name"
              />
            </div>
            <div className="space-y-3 flex flex-col justify-end pb-1">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                <div className="flex items-center gap-2">
                  {formData.show_business_name ? (
                    <Eye className="w-4 h-4 text-indigo-600" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-slate-400" />
                  )}
                  <div className="flex flex-col">
                    <Label className="text-sm font-medium leading-none cursor-pointer" htmlFor="show_business_name">
                      Display Name
                    </Label>
                    <span className="text-[10px] text-slate-500">Show name in header/footer</span>
                  </div>
                </div>
                <Switch
                  id="show_business_name"
                  checked={formData.show_business_name}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, show_business_name: checked }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Store Logo</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.logo_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, logo_url: e.target.value }))}
                  placeholder="Paste URL or upload..."
                  className="h-12 flex-1"
                  data-testid="input-logo-url"
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 px-4"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-slate-500">Upload JPG, PNG, GIF or WebP (max 5MB)</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="business_description">Description</Label>
            <Textarea
              id="business_description"
              value={formData.business_description}
              onChange={(e) => setFormData(prev => ({ ...prev, business_description: e.target.value }))}
              placeholder="Tell customers about your business..."
              rows={3}
              data-testid="input-description"
            />
          </div>

          {formData.logo_url && (
            <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
              <img
                src={getImageUrl(formData.logo_url)}
                alt="Logo preview"
                className="w-16 h-16 rounded-xl object-cover"
                onError={(e) => e.target.style.display = 'none'}
              />
              <p className="text-sm text-slate-600">Logo preview</p>
            </div>
          )}
        </div>

        {/* Contact Info */}
        <div className="card-elevated p-6 space-y-4">
          <h3 className="text-lg font-semibold text-slate-900">
            Contact Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contact_email">Email</Label>
              <Input
                id="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                className="h-12"
                data-testid="input-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_phone">Phone</Label>
              <Input
                id="contact_phone"
                value={formData.contact_phone}
                onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                className="h-12"
                data-testid="input-phone"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="business_address">Address*</Label>
            <Input
              id="business_address"
              value={formData.business_address?.address || ""}
              onChange={(e) => setFormData(prev => ({ ...prev, business_address: { ...prev.business_address, address: e.target.value } }))}
              placeholder="Street address"
              className="h-12"
              data-testid="input-address"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="business_city">Town / City*</Label>
            <Input
              id="business_city"
              value={formData.business_address?.city || ""}
              onChange={(e) => setFormData(prev => ({ ...prev, business_address: { ...prev.business_address, city: e.target.value } }))}
              placeholder="City"
              className="h-12"
              data-testid="input-city"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Country*</Label>
                <Select
                  value={formData.business_address?.country || "US"}
                  onValueChange={(val) => {
                    setFormData(prev => ({ 
                      ...prev, 
                      business_address: { ...prev.business_address, country: val, state: "" } 
                    }));
                  }}
                >
                  <SelectTrigger className="h-12 border-slate-200" data-testid="select-country">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
            </div>
            <div className="space-y-2">
              <Label>State*</Label>
              <Popover open={stateDropdownOpen} onOpenChange={setStateDropdownOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={stateDropdownOpen}
                    className={cn(
                      "w-full justify-between h-12 border-slate-200 rounded-md font-normal",
                      !formData.business_address?.state && "text-muted-foreground"
                    )}
                  >
                    {formData.business_address?.state
                      ? [...US_STATES, ...CA_PROVINCES].find((s) => s.value === formData.business_address.state)?.label
                      : formData.business_address?.country === 'CA' ? "Choose a Province" : "Choose a State"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder={formData.business_address?.country === 'CA' ? "Search province..." : "Search state..."} className="h-9" />
                    <CommandEmpty>{formData.business_address?.country === 'CA' ? "No province found." : "No state found."}</CommandEmpty>
                    <CommandList>
                      <CommandGroup>
                        {(formData.business_address?.country === 'CA' ? CA_PROVINCES : US_STATES).map((state) => (
                          <CommandItem
                            key={state.value}
                            value={state.label}
                            onSelect={() => {
                              setFormData(prev => ({ ...prev, business_address: { ...prev.business_address, state: state.value } }));
                              setStateDropdownOpen(false);
                            }}
                          >
                            {state.label}
                            <Check
                              className={cn(
                                "ml-auto h-4 w-4",
                                formData.business_address?.state === state.value ? "opacity-100" : "opacity-0"
                              )}
                            />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Zipcode*</Label>
              <Input
                value={formData.business_address?.zipcode || ""}
                onChange={(e) => setFormData(prev => ({ ...prev, business_address: { ...prev.business_address, zipcode: e.target.value } }))}
                placeholder="Zipcode"
                className="h-12"
                data-testid="input-zipcode"
              />
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="card-elevated p-6 space-y-4">
          <h3 className="text-lg font-semibold text-slate-900">
            Social Links
          </h3>

          <div className="space-y-3">
            {socialPlatforms.map((platform) => (
              <div key={platform.key} className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <platform.icon className="w-5 h-5 text-slate-600" />
                </div>
                <Input
                  value={formData.social_links[platform.key] || ""}
                  onChange={(e) => handleSocialLinkChange(platform.key, e.target.value)}
                  placeholder={`${platform.label} URL`}
                  className="h-12 flex-1"
                  data-testid={`input-social-${platform.key}`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Theme Customization */}
        <div className="card-elevated p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Palette className="w-5 h-5 text-amber-600" />
            <h3 className="text-lg font-semibold text-slate-900">
              Store Theme
            </h3>
          </div>
          <p className="text-sm text-slate-500">Choose a theme preset for your store appearance</p>

          {loadingPresets ? (
            <div className="flex items-center justify-center py-12 bg-slate-50 rounded-xl border border-dashed">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {Object.entries(themePresets).map(([key, preset]) => (
                <div
                  key={key}
                  className={`p-4 rounded-xl cursor-pointer border-2 transition-all ${formData.theme?.preset === key ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-slate-300'
                    }`}
                  onClick={() => setFormData(prev => ({ 
                    ...prev, 
                    theme: { 
                      ...prev.theme, 
                      preset: key,
                      custom_primary_color: "",
                      custom_accent_color: "",
                      custom_secondary_color: "",
                      custom_background_color: "",
                      custom_header_bg_color: "",
                      custom_text_color: "",
                      custom_font_family: ""
                    } 
                  }))}
                  data-testid={`theme-${key}`}
                >
                  <div className="flex gap-1 mb-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: preset.primary_color }}></div>
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: preset.accent_color }}></div>
                    <div className="w-4 h-4 rounded border" style={{ backgroundColor: preset.background_color }}></div>
                  </div>
                  <p className="text-sm font-medium">{preset.name}</p>
                  <p className="text-xs text-slate-500">{preset.font_family?.split(',')[0]}</p>
                  <div className="mt-1 h-1 w-full rounded-full" style={{ backgroundColor: preset.header_bg_color || preset.primary_color }}></div>
                </div>
              ))}
            </div>
          )}

          {/* Custom Color Overrides */}
          <div className="mt-6 p-4 bg-slate-50 rounded-xl space-y-4">
            <h4 className="font-medium text-slate-900">Custom Color Overrides (Optional)</h4>
            <p className="text-xs text-slate-500">Override preset colors with your brand colors</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Primary Color</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.theme?.custom_primary_color || themePresets[formData.theme?.preset]?.primary_color || '#1a365d'}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      theme: { ...prev.theme, custom_primary_color: e.target.value }
                    }))}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={formData.theme?.custom_primary_color || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      theme: { ...prev.theme, custom_primary_color: e.target.value }
                    }))}
                    placeholder="Use preset"
                    className="h-10 flex-1 text-xs"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Accent Color</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.theme?.custom_accent_color || themePresets[formData.theme?.preset]?.accent_color || '#d69e2e'}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      theme: { ...prev.theme, custom_accent_color: e.target.value }
                    }))}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={formData.theme?.custom_accent_color || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      theme: { ...prev.theme, custom_accent_color: e.target.value }
                    }))}
                    placeholder="Use preset"
                    className="h-10 flex-1 text-xs"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Background</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.theme?.custom_background_color || themePresets[formData.theme?.preset]?.background_color || '#ffffff'}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      theme: { ...prev.theme, custom_background_color: e.target.value }
                    }))}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={formData.theme?.custom_background_color || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      theme: { ...prev.theme, custom_background_color: e.target.value }
                    }))}
                    placeholder="Use preset"
                    className="h-10 flex-1 text-xs"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Text Color</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.theme?.custom_text_color || '#1a202c'}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      theme: { ...prev.theme, custom_text_color: e.target.value }
                    }))}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={formData.theme?.custom_text_color || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      theme: { ...prev.theme, custom_text_color: e.target.value }
                    }))}
                    placeholder="Use preset"
                    className="h-10 flex-1 text-xs"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Header Background</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.theme?.custom_header_bg_color || themePresets[formData.theme?.preset]?.header_bg_color || themePresets[formData.theme?.preset]?.primary_color || '#1a365d'}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      theme: { ...prev.theme, custom_header_bg_color: e.target.value }
                    }))}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={formData.theme?.custom_header_bg_color || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      theme: { ...prev.theme, custom_header_bg_color: e.target.value }
                    }))}
                    placeholder="Use preset"
                    className="h-10 flex-1 text-xs"
                  />
                </div>
              </div>
            </div>
            {(formData.theme?.custom_primary_color || formData.theme?.custom_accent_color ||
              formData.theme?.custom_background_color || formData.theme?.custom_text_color || formData.theme?.custom_header_bg_color) && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    theme: {
                      preset: prev.theme?.preset || 'classic_elegant',
                      custom_primary_color: "",
                      custom_accent_color: "",
                      custom_background_color: "",
                      custom_header_bg_color: "",
                      custom_text_color: ""
                    }
                  }))}
                  className="text-xs hover:bg-slate-100/80"
                >
                  <X className="w-3 h-3 mr-1" /> Reset to Preset Colors
                </Button>
              )}
          </div>

          {/* Theme Preview */}
          <div className="pt-4 border-t border-slate-100">
            <Label className="text-xs font-semibold text-slate-500 mb-3 block uppercase tracking-wider">Live Store Preview</Label>
            <div 
              className="rounded-xl p-6 border flex flex-col gap-5 shadow-sm"
              style={{ 
                backgroundColor: formData.theme?.custom_background_color || themePresets[formData.theme?.preset]?.background_color || '#ffffff',
                fontFamily: formData.theme?.custom_font_family || themePresets[formData.theme?.preset]?.font_family || 'Inter'
              }}
            >
              {(() => {
                const getContrast = (hex) => {
                  if (!hex || hex === 'transparent') return 'white';
                  const r = parseInt(hex.slice(1, 3), 16);
                  const g = parseInt(hex.slice(3, 5), 16);
                  const b = parseInt(hex.slice(5, 7), 16);
                  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
                  return (yiq >= 128) ? 'black' : 'white';
                };

                const primaryBg = formData.theme?.custom_primary_color || themePresets[formData.theme?.preset]?.primary_color || '#1a365d';
                const headerBg = formData.theme?.custom_header_bg_color || themePresets[formData.theme?.preset]?.header_bg_color || primaryBg;
                const accentBg = formData.theme?.custom_accent_color || themePresets[formData.theme?.preset]?.accent_color || '#d69e2e';
                const textCol = formData.theme?.custom_text_color || themePresets[formData.theme?.preset]?.text_color || '#1a202c';

                return (
                  <div className="space-y-4">
                    <div
                      className="h-12 w-full rounded-lg flex items-center px-4 shadow-sm border"
                      style={{ 
                        backgroundColor: headerBg,
                        color: getContrast(headerBg)
                      }}
                    >
                      <span className="text-sm font-bold tracking-tight">Header Preview</span>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div
                        className="px-5 py-2 rounded-full text-sm font-bold shadow-sm"
                        style={{ 
                          backgroundColor: primaryBg,
                          color: getContrast(primaryBg)
                        }}
                      >
                        Primary Button
                      </div>
                      <div
                        className="px-5 py-2 rounded-full text-sm font-bold shadow-sm"
                        style={{ 
                          backgroundColor: accentBg,
                          color: getContrast(accentBg)
                        }}
                      >
                        Accent Action
                      </div>
                    </div>

                    <div className="flex flex-col pt-2">
                      <p className="text-sm font-semibold" style={{ color: textCol }}>
                        Typography Preview
                      </p>
                      <p className="text-xs opacity-70" style={{ color: textCol }}>
                        This is a preview of how your store will look with the current theme settings.
                      </p>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        <Button
          type="submit"
          disabled={saving}
          className="bg-indigo-600 hover:bg-indigo-700 rounded-full px-8 h-12"
          data-testid="save-settings-btn"
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </form>
    </div>
  );
}
